import { getLocalActiveSession } from "./activeSession";
import {
  getActiveCacheOrganizationId,
  readOrgScopedJson,
  writeOrgScopedJson,
} from "./orgCacheStorage";
import { getReviewSession } from "./reviewSession";
import {
  associateSessionMaterialTimeLog,
  commitMaterialUsageBatch,
  loadMaterialUsageForSession,
  mapMaterialCommitUserError,
  MATERIAL_USAGE_SOURCE,
  updateCommittedMaterialUsage,
  voidCommittedMaterialUsage,
} from "./projectMaterialLedger";
import {
  loadTimelogBySessionId,
  resolveTimeLogId,
} from "./projectLabourLedger";
import { requireMaterialUsageVoid } from "./financialGuards";
import { isUuidString } from "./uuidFields";
import { devLog } from "./devLog";
import {
  assertProjectOperationallyWritable,
  OPERATIONAL_LOCK_USER_MESSAGE,
} from "./operationalLifecycle";

export { resolveTimeLogId };

const STORAGE_KEY = "CP_SESSION_MATERIALS";

export const SESSION_MATERIAL_SYNC_STATUS = Object.freeze({
  PENDING: "pending",
  SYNCING: "syncing",
  SAVED: "saved",
  ERROR: "error",
});

const syncTails = new Map();

/**
 * Durable Persistence Rule — materials:
 * Absence of error is not enough. Local drafts may be cleared only after a
 * positive durable acknowledgment (valid commitBatchId, or verified
 * server-side usage rows for the session).
 */
export function isConfirmedMaterialCommit(result) {
  if (!result || result.error) {
    return false;
  }
  if (!result.commitBatchId) {
    return false;
  }
  if (Array.isArray(result.lines) && result.lines.length > 0) {
    return result.lines.every((line) => line.usageId || line.usage_id);
  }
  return true;
}

/**
 * True once the server holds a ledger row for this line (even when a later
 * edit is still syncing or failed). Voiding such a line is a ledger void and
 * needs CAPABILITY.MATERIAL_USAGE_VOID; a purely local draft does not.
 */
export function isSessionMaterialLineCommitted(line) {
  return Boolean(line && line.usage_id && !line.voided_at);
}

export function isSessionMaterialLineSaved(line) {
  return Boolean(
    line &&
      line.server_synced === true &&
      line.usage_id &&
      line.commit_batch_id &&
      line.sync_status === SESSION_MATERIAL_SYNC_STATUS.SAVED &&
      !line.voided_at,
  );
}

/**
 * Confirm session material lines exist on the server (positive persistence).
 */
export async function confirmSessionMaterialUsageOnServer(
  sessionId,
  { minLines = 1, timeLogId = null, actor = null } = {},
) {
  if (!sessionId) {
    return { confirmed: false, rows: [], commitBatchId: null };
  }

  // The actor selects the relation: employees read the cost-free operational
  // projection (the base ledger is manager+ only), managers keep the ledger.
  const rows = await loadMaterialUsageForSession(sessionId, { actor });
  const activeRows = (rows || []).filter((row) => !row.voided_at);

  if (minLines < 1 && activeRows.length === 0) {
    return { confirmed: true, rows: [], commitBatchId: null };
  }

  if (activeRows.length < Math.max(minLines, 1)) {
    return { confirmed: false, rows: activeRows, commitBatchId: null };
  }

  const commitBatchId =
    activeRows.find((row) => row?.commit_batch_id)?.commit_batch_id || null;

  if (timeLogId != null && timeLogId !== "") {
    const parsed = Number(timeLogId);
    if (
      !Number.isFinite(parsed) ||
      !activeRows.every((row) => Number(row.time_log_id) === parsed)
    ) {
      return { confirmed: false, rows: activeRows, commitBatchId };
    }
  }

  return {
    confirmed: true,
    rows: activeRows,
    commitBatchId,
  };
}

function readStore(organizationId = null) {
  try {
    const orgId = organizationId ?? getActiveCacheOrganizationId();
    return readOrgScopedJson(STORAGE_KEY, orgId) || {};
  } catch {
    return {};
  }
}

function writeStore(store, organizationId = null) {
  const orgId = organizationId ?? getActiveCacheOrganizationId();
  writeOrgScopedJson(STORAGE_KEY, orgId, store || {});
}

export function getSessionMaterials(sessionId) {
  if (!sessionId) return [];
  return readStore()[sessionId] || [];
}

export function saveSessionMaterials(sessionId, lines) {
  if (!sessionId) return;

  const store = readStore();

  if (!lines?.length) {
    delete store[sessionId];
  } else {
    store[sessionId] = lines;
  }

  writeStore(store);
}

export function clearSessionMaterials(sessionId) {
  if (!sessionId) return;

  const store = readStore();
  delete store[sessionId];
  writeStore(store);
}

export function computeSessionMaterialLineTotal(line) {
  const qty = Number(line?.quantity);
  const rate = Number(line?.unit_cost_snapshot);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  if (!Number.isFinite(rate)) return 0;
  return Math.round(qty * rate * 100) / 100;
}

export function computeSessionMaterialsTotal(lines) {
  return (lines || []).reduce(
    (sum, line) => sum + computeSessionMaterialLineTotal(line),
    0,
  );
}

export function getSessionMaterialLineIdempotencyKey(line) {
  if (isUuidString(line?.idempotency_key)) {
    return String(line.idempotency_key).trim();
  }
  if (isUuidString(line?.localId)) {
    return String(line.localId).trim();
  }
  return null;
}

export function normalizeSessionMaterialLine(line = {}) {
  const revision = Number.isFinite(Number(line.revision))
    ? Number(line.revision)
    : 0;
  const saved = Boolean(
    line.server_synced && line.usage_id && line.commit_batch_id,
  );
  return {
    ...line,
    revision,
    sync_status:
      line.sync_status ||
      (saved
        ? SESSION_MATERIAL_SYNC_STATUS.SAVED
        : SESSION_MATERIAL_SYNC_STATUS.PENDING),
    sync_error: line.sync_error || null,
    server_synced: saved,
    idempotency_key: getSessionMaterialLineIdempotencyKey(line),
  };
}

export function buildMaterialDraftFromCatalog(material, quantity, notes, usageDate) {
  const localId = crypto.randomUUID();
  return normalizeSessionMaterialLine({
    localId,
    idempotency_key: localId,
    material_id: material.material_id,
    quantity: Number(quantity),
    material_name_snapshot: material.material_name,
    sku_snapshot: material.sku || null,
    unit_of_measure_snapshot: material.unit_of_measure || null,
    notes: notes?.trim() || "",
    usage_date: usageDate,
    server_synced: false,
    usage_id: null,
    commit_batch_id: null,
    revision: 1,
    sync_status: SESSION_MATERIAL_SYNC_STATUS.PENDING,
  });
}

export function mergeSessionMaterialLine(existing, incoming) {
  if (!existing) {
    return normalizeSessionMaterialLine(incoming);
  }
  if (!incoming) {
    return normalizeSessionMaterialLine(existing);
  }

  const existingRevision = Number(existing.revision || 0);
  const incomingRevision = Number(incoming.revision || 0);
  const newerLocal = existingRevision > incomingRevision;
  const base = newerLocal ? existing : { ...existing, ...incoming };
  const patch = newerLocal ? incoming : existing;

  return normalizeSessionMaterialLine({
    ...base,
    usage_id: base.usage_id || patch.usage_id || null,
    commit_batch_id: base.commit_batch_id || patch.commit_batch_id || null,
    idempotency_key:
      getSessionMaterialLineIdempotencyKey(base) ||
      getSessionMaterialLineIdempotencyKey(patch),
  });
}

export function reconcileSessionMaterialLines(currentLines, incomingLines = []) {
  const byId = new Map();
  for (const line of currentLines || []) {
    if (line?.localId) {
      byId.set(line.localId, normalizeSessionMaterialLine(line));
    }
  }
  for (const incoming of incomingLines || []) {
    if (!incoming?.localId) continue;
    byId.set(
      incoming.localId,
      mergeSessionMaterialLine(byId.get(incoming.localId), incoming),
    );
  }
  return [...byId.values()];
}

function patchSessionMaterialLine(sessionId, localId, patch) {
  if (!sessionId || !localId) {
    return getSessionMaterials(sessionId);
  }

  const current = getSessionMaterials(sessionId);
  const next = current.map((line) => {
    if (line.localId !== localId) {
      return line;
    }
    return mergeSessionMaterialLine(line, {
      ...line,
      ...patch,
      localId,
      revision:
        patch.revision == null ? line.revision : Number(patch.revision),
    });
  });
  saveSessionMaterials(sessionId, next);
  return next;
}

export function upsertSessionMaterialLine(sessionId, line) {
  if (!sessionId || !line?.localId) {
    return getSessionMaterials(sessionId);
  }

  const current = getSessionMaterials(sessionId);
  const existing = current.find((item) => item.localId === line.localId);
  const { bumpRevision, ...rest } = line;
  const revision = existing
    ? Number(existing.revision || 0) + (bumpRevision ? 1 : 0)
    : Number(line.revision || 1);
  const nextLine = normalizeSessionMaterialLine({
    ...existing,
    ...rest,
    revision: revision || 1,
  });
  const next = existing
    ? current.map((item) =>
        item.localId === line.localId
          ? mergeSessionMaterialLine(item, nextLine)
          : item,
      )
    : [...current, nextLine];
  saveSessionMaterials(sessionId, next);
  return next;
}

export function removeSessionMaterialDraft(sessionId, localId) {
  if (!sessionId || !localId) {
    return getSessionMaterials(sessionId);
  }
  const next = getSessionMaterials(sessionId).filter(
    (line) => line.localId !== localId,
  );
  saveSessionMaterials(sessionId, next);
  return next;
}

export function getUnsyncedSessionMaterialLines(lines = []) {
  return (lines || []).filter((line) => !isSessionMaterialLineSaved(line) && !line.voided_at);
}

function enqueueSessionMaterialSync(sessionId, task) {
  const previous = syncTails.get(sessionId) || Promise.resolve();
  const next = previous.then(
    () => task(),
    () => task(),
  );
  const guarded = next.catch((error) => {
    devLog("SESSION_MATERIAL_SYNC_QUEUE_ERROR", {
      sessionId,
      errorName: error?.name || null,
    });
  });
  syncTails.set(sessionId, guarded);
  return next;
}

export async function flushSessionMaterialSync(sessionId) {
  if (!sessionId) return;
  await (syncTails.get(sessionId) || Promise.resolve());
}

async function commitOneSessionMaterialLine({
  sessionId,
  projectId,
  enteredBy,
  usageDate,
  timeLogId = null,
  line,
  source = MATERIAL_USAGE_SOURCE.SESSION_INCREMENTAL,
}) {
  const live = getSessionMaterials(sessionId).find(
    (item) => item.localId === line.localId,
  );
  if (!live || isSessionMaterialLineSaved(live) || live.voided_at) {
    return { line: live || line, error: null };
  }

  const idempotencyKey = getSessionMaterialLineIdempotencyKey(live);
  if (!idempotencyKey) {
    const error = new Error("Materials could not be saved. Please retry.");
    patchSessionMaterialLine(sessionId, live.localId, {
      sync_status: SESSION_MATERIAL_SYNC_STATUS.ERROR,
      sync_error: error.message,
      server_synced: false,
    });
    return { line: live, error };
  }

  const expectedRevision = Number(live.revision || 0);
  patchSessionMaterialLine(sessionId, live.localId, {
    sync_status: SESSION_MATERIAL_SYNC_STATUS.SYNCING,
    sync_error: null,
    revision: expectedRevision,
  });

  const result = await commitMaterialUsageBatch({
    idempotencyKey,
    sessionId,
    projectId,
    enteredBy,
    timeLogId,
    usageDate: live.usage_date || usageDate,
    lines: [live],
    source,
  });

  const committed = (result.lines || []).find(
    (row) =>
      row.clientLineId === live.localId ||
      row.materialId === live.material_id,
  );
  const usageId = committed?.usageId || null;
  const commitBatchId = result.commitBatchId || committed?.commitBatchId || null;

  if (!isConfirmedMaterialCommit(result) || !usageId) {
    const error =
      result.error ||
      new Error("Materials could not be saved. Please retry.");
    patchSessionMaterialLine(sessionId, live.localId, {
      sync_status: SESSION_MATERIAL_SYNC_STATUS.ERROR,
      sync_error: error.message,
      server_synced: false,
      usage_id: usageId || live.usage_id || null,
      commit_batch_id: commitBatchId || live.commit_batch_id || null,
      revision: expectedRevision,
    });
    return { line: live, error };
  }

  const latest = getSessionMaterials(sessionId).find(
    (item) => item.localId === live.localId,
  );
  const newerEdit = Number(latest?.revision || 0) > expectedRevision;

  patchSessionMaterialLine(sessionId, live.localId, {
    usage_id: usageId,
    commit_batch_id: commitBatchId,
    server_synced: !newerEdit,
    sync_status: newerEdit
      ? SESSION_MATERIAL_SYNC_STATUS.PENDING
      : SESSION_MATERIAL_SYNC_STATUS.SAVED,
    sync_error: null,
    revision: latest?.revision ?? expectedRevision,
  });

  return { line: latest || live, error: null };
}

/**
 * Push unsynced session material lines to the durable RPC.
 * Always re-reads the org-scoped store so an older completion cannot erase
 * a newer local draft. Commits are serialized per session.
 */
export async function syncUnsyncedSessionMaterialLines({
  sessionId,
  projectId,
  enteredBy,
  usageDate,
  timeLogId = null,
  source = MATERIAL_USAGE_SOURCE.SESSION_INCREMENTAL,
} = {}) {
  if (!sessionId || !projectId || !enteredBy) {
    return { lines: getSessionMaterials(sessionId), error: null };
  }

  return enqueueSessionMaterialSync(sessionId, async () => {
    let lastError = null;
    const pending = getUnsyncedSessionMaterialLines(getSessionMaterials(sessionId));

    for (const line of pending) {
      const { error } = await commitOneSessionMaterialLine({
        sessionId,
        projectId,
        enteredBy,
        usageDate,
        timeLogId,
        line,
        source,
      });
      if (error) {
        lastError = error;
      }
    }

    return {
      lines: getSessionMaterials(sessionId),
      error: lastError,
    };
  });
}

/**
 * Commit session materials via commit-batch idempotency (not session-scoped dedup).
 * When `force` is true, unsaved lines are retried (already saved lines are skipped).
 */
export async function commitSessionMaterials({
  sessionId,
  projectId,
  enteredBy,
  timeLogId,
  usageDate,
  lines: linesOverride,
  idempotencyKey,
  force = false,
  source = MATERIAL_USAGE_SOURCE.FINALIZE_SESSION,
  project = null,
  client = null,
}) {
  if (project || client) {
    try {
      assertProjectOperationallyWritable(
        project || {
          project_id: projectId,
          client_id: client?.client_id || null,
          is_active: true,
          status: "active",
          clients: client || undefined,
        },
        client,
      );
    } catch (lockError) {
      return {
        error: new Error(lockError?.message || OPERATIONAL_LOCK_USER_MESSAGE),
        skipped: false,
        commitBatchId: null,
        lines: [],
      };
    }
  }

  if (linesOverride?.length) {
    const current = getSessionMaterials(sessionId);
    saveSessionMaterials(
      sessionId,
      reconcileSessionMaterialLines(current, linesOverride),
    );
  }

  const allLines = getSessionMaterials(sessionId);
  const lines = force
    ? (allLines || []).filter((line) => !line.voided_at)
    : getUnsyncedSessionMaterialLines(allLines);

  if (!lines.length) {
    return { error: null, skipped: true, commitBatchId: null, lines: [] };
  }

  void idempotencyKey;

  return syncUnsyncedSessionMaterialLines({
    sessionId,
    projectId,
    enteredBy,
    usageDate,
    timeLogId,
    source,
  }).then((result) => {
    const saved = (result.lines || []).filter(isSessionMaterialLineSaved);
    const unsaved = getUnsyncedSessionMaterialLines(result.lines);
    if (unsaved.length) {
      return {
        error:
          result.error ||
          new Error("Materials could not be saved. Please retry."),
        skipped: false,
        commitBatchId: saved[0]?.commit_batch_id || null,
        lines: saved.map((line) => ({
          clientLineId: line.localId,
          usageId: line.usage_id,
          commitBatchId: line.commit_batch_id,
        })),
      };
    }

    return {
      error: null,
      skipped: false,
      commitBatchId: saved[0]?.commit_batch_id || null,
      lines: saved.map((line) => ({
        clientLineId: line.localId,
        usageId: line.usage_id,
        commitBatchId: line.commit_batch_id,
      })),
    };
  });
}

/**
 * Commit session materials and require positive durable confirmation before
 * callers clear local drafts / queue rows.
 */
export async function commitSessionMaterialsDurably({
  sessionId,
  projectId,
  enteredBy,
  timeLogId,
  usageDate,
  lines: linesOverride,
  idempotencyKey,
  source = MATERIAL_USAGE_SOURCE.FINALIZE_SESSION,
  actor = null,
}) {
  await flushSessionMaterialSync(sessionId);

  if (linesOverride?.length) {
    saveSessionMaterials(
      sessionId,
      reconcileSessionMaterialLines(getSessionMaterials(sessionId), linesOverride),
    );
  }

  const seeded = getSessionMaterials(sessionId);
  const unsynced = getUnsyncedSessionMaterialLines(seeded);

  let result = {
    error: null,
    skipped: !unsynced.length,
    commitBatchId: seeded.find((line) => line.commit_batch_id)?.commit_batch_id || null,
    lines: [],
  };

  if (unsynced.length) {
    result = await commitSessionMaterials({
      sessionId,
      projectId,
      enteredBy,
      timeLogId,
      usageDate,
      lines: seeded,
      idempotencyKey,
      source,
    });
  }

  const remainingUnsynced = getUnsyncedSessionMaterialLines(
    getSessionMaterials(sessionId),
  );
  if (remainingUnsynced.length) {
    return {
      ...result,
      error:
        result.error ||
        new Error("Materials could not be saved. Please retry."),
      confirmed: false,
    };
  }

  const localActive = getSessionMaterials(sessionId).filter(
    (line) => !line.voided_at,
  );
  const parsedTimeLogId =
    timeLogId == null || timeLogId === "" ? null : Number(timeLogId);

  const existingServer = await confirmSessionMaterialUsageOnServer(sessionId, {
    minLines: 0,
    actor,
  });
  if (!localActive.length && !(existingServer.rows || []).length) {
    return {
      error: null,
      skipped: true,
      commitBatchId: null,
      confirmed: true,
      lines: [],
    };
  }

  if (parsedTimeLogId) {
    const associated = await associateSessionMaterialTimeLog({
      sessionId,
      timeLogId: parsedTimeLogId,
    });
    if (associated.error) {
      return {
        error: associated.error,
        skipped: Boolean(result.skipped),
        commitBatchId: result.commitBatchId || null,
        confirmed: false,
        lines: result.lines || [],
      };
    }
  }

  const server = await confirmSessionMaterialUsageOnServer(sessionId, {
    minLines: localActive.length,
    timeLogId: parsedTimeLogId,
    actor,
  });

  if (!parsedTimeLogId || !server.confirmed) {
    return {
      error:
        result.error ||
        new Error("Materials could not be saved. Please retry."),
      skipped: Boolean(result.skipped),
      commitBatchId: server.commitBatchId || result.commitBatchId || null,
      confirmed: false,
      lines: result.lines || [],
    };
  }

  return {
    error: null,
    skipped: Boolean(result.skipped),
    commitBatchId: server.commitBatchId || result.commitBatchId || null,
    confirmed: true,
    lines: result.lines || [],
  };
}

export async function persistSessionMaterialLineEdit({
  sessionId,
  localId,
  quantity,
  notes,
  enteredBy,
}) {
  return enqueueSessionMaterialSync(sessionId, async () => {
  const current = getSessionMaterials(sessionId).find(
    (line) => line.localId === localId,
  );
  if (!current) {
    throw new Error("Material line not found.");
  }

  const parsedQuantity = Number(quantity);
  if (!parsedQuantity || parsedQuantity <= 0) {
    throw new Error("Quantity must be greater than zero.");
  }

  const nextNotes = notes?.trim() || "";

  if (!isSessionMaterialLineSaved(current)) {
    return upsertSessionMaterialLine(sessionId, {
      ...current,
      quantity: parsedQuantity,
      notes: nextNotes,
      bumpRevision: true,
      sync_status: SESSION_MATERIAL_SYNC_STATUS.PENDING,
      sync_error: null,
      server_synced: false,
    });
  }

  upsertSessionMaterialLine(sessionId, {
    ...current,
    quantity: parsedQuantity,
    notes: nextNotes,
    bumpRevision: true,
    sync_status: SESSION_MATERIAL_SYNC_STATUS.SYNCING,
    sync_error: null,
  });

  const expectedRevision = Number(
    getSessionMaterials(sessionId).find((line) => line.localId === localId)
      ?.revision || 0,
  );

  try {
    await updateCommittedMaterialUsage({
      usageId: current.usage_id,
      quantity: parsedQuantity,
      notes: nextNotes,
    });
    const latest = getSessionMaterials(sessionId).find(
      (item) => item.localId === localId,
    );
    if (Number(latest?.revision || 0) > expectedRevision) {
      return latest;
    }
    return patchSessionMaterialLine(sessionId, localId, {
      quantity: parsedQuantity,
      notes: nextNotes,
      sync_status: SESSION_MATERIAL_SYNC_STATUS.SAVED,
      sync_error: null,
      server_synced: true,
      revision: expectedRevision,
    });
  } catch (error) {
    const mapped = mapMaterialCommitUserError(
      error,
      "Materials could not be saved. Please retry.",
    );
    const latest = getSessionMaterials(sessionId).find(
      (item) => item.localId === localId,
    );
    if (Number(latest?.revision || 0) > expectedRevision) {
      throw mapped;
    }
    patchSessionMaterialLine(sessionId, localId, {
      quantity: parsedQuantity,
      notes: nextNotes,
      sync_status: SESSION_MATERIAL_SYNC_STATUS.ERROR,
      sync_error: mapped.message,
      server_synced: true,
      revision: expectedRevision,
    });
    throw mapped;
  } finally {
    void enteredBy;
  }
  });
}

export async function persistSessionMaterialLineRemoval({
  sessionId,
  localId,
  voidedBy,
  actor = null,
}) {
  return enqueueSessionMaterialSync(sessionId, async () => {
  const current = getSessionMaterials(sessionId).find(
    (line) => line.localId === localId,
  );
  if (!current) {
    return getSessionMaterials(sessionId);
  }

  if (!isSessionMaterialLineSaved(current)) {
    return removeSessionMaterialDraft(sessionId, localId);
  }

  // Defence in depth: the UI hides Remove for actors who cannot void, and the
  // ledger trigger rejects them. Fail before any request is made.
  if (actor) {
    requireMaterialUsageVoid(actor);
  }

  patchSessionMaterialLine(sessionId, localId, {
    sync_status: SESSION_MATERIAL_SYNC_STATUS.SYNCING,
    sync_error: null,
  });

  try {
    await voidCommittedMaterialUsage({
      usageId: current.usage_id,
      voidedBy,
    });
    return removeSessionMaterialDraft(sessionId, localId);
  } catch (error) {
    const mapped = mapMaterialCommitUserError(
      error,
      "Materials could not be saved. Please retry.",
    );
    patchSessionMaterialLine(sessionId, localId, {
      sync_status: SESSION_MATERIAL_SYNC_STATUS.ERROR,
      sync_error: mapped.message,
    });
    throw mapped;
  }
  });
}

/**
 * List all local session material drafts keyed by session id.
 */
export function listSessionMaterialDrafts() {
  const store = readStore();

  return Object.entries(store)
    .map(([sessionId, lines]) => ({
      sessionId,
      lines: lines || [],
      lineCount: (lines || []).length,
      estimatedCost: computeSessionMaterialsTotal(lines || []),
    }))
    .filter((entry) => entry.lineCount > 0);
}

/**
 * Session ids that are still in the normal clock-in / review / finalize flow.
 * Recovery must not interrupt these — only genuinely stale drafts.
 */
export function getMaterialRecoveryExcludedSessionIds({
  activeSession = null,
  reviewSession = getReviewSession(),
  additionalExcludedSessionIds = [],
  supabaseUserId = null,
} = {}) {
  const resolvedActiveSession =
    activeSession ??
    (supabaseUserId ? getLocalActiveSession(supabaseUserId) : null);

  return [
    ...new Set(
      [
        resolvedActiveSession?.session_id,
        reviewSession?.session_id,
        ...additionalExcludedSessionIds,
      ].filter(Boolean),
    ),
  ];
}

export function shouldExcludeDraftFromMaterialRecovery(
  sessionId,
  excludedSessionIds = [],
) {
  return Boolean(sessionId && excludedSessionIds.includes(sessionId));
}

/**
 * Detect material drafts left in localStorage after their session timelog exists
 * but materials were never committed (abandoned / interrupted finalize).
 */
export async function detectAbandonedSessionMaterialDrafts({
  pendingTimeLogs = [],
  excludedSessionIds = null,
  supabaseUserId = null,
} = {}) {
  const drafts = listSessionMaterialDrafts();
  const abandoned = [];
  const skipSessionIds =
    excludedSessionIds ??
    getMaterialRecoveryExcludedSessionIds({ supabaseUserId });

  for (const draft of drafts) {
    if (shouldExcludeDraftFromMaterialRecovery(draft.sessionId, skipSessionIds)) {
      continue;
    }

    const pendingLog = pendingTimeLogs.find(
      (log) => log.session_id === draft.sessionId,
    );

    if (
      pendingLog?.pending_materials?.length &&
      !pendingLog.materials_synced
    ) {
      continue;
    }

    const timelog = await loadTimelogBySessionId(draft.sessionId);

    if (!timelog) {
      abandoned.push({
        ...draft,
        reason: "no_timelog",
        timeLogId: null,
        projectId: pendingLog?.project_id || null,
        enteredBy: pendingLog?.supabase_user_id || pendingLog?.user_id || null,
        usageDate:
          pendingLog?.date_iso ||
          draft.lines[0]?.usage_date ||
          new Date().toISOString().slice(0, 10),
      });
      continue;
    }

    abandoned.push({
      ...draft,
      reason: "uncommitted",
      timeLogId: timelog.id,
      projectId: timelog.project_id || pendingLog?.project_id || null,
      enteredBy: timelog.supabase_user_id || pendingLog?.supabase_user_id || null,
      usageDate:
        timelog.date_iso ||
        draft.lines[0]?.usage_date ||
        new Date().toISOString().slice(0, 10),
    });
  }

  return abandoned;
}

export async function recoverAbandonedSessionMaterials({
  sessionId,
  projectId,
  enteredBy,
  timeLogId,
  usageDate,
  lines,
}) {
  if (!sessionId || !projectId || !enteredBy || !lines?.length) {
    throw new Error("sessionId, projectId, enteredBy, and lines are required.");
  }

  const result = await commitSessionMaterialsDurably({
    sessionId,
    projectId,
    enteredBy,
    timeLogId,
    usageDate,
    lines,
    source: MATERIAL_USAGE_SOURCE.ABANDONED_RECOVERY,
  });

  const remaining = getUnsyncedSessionMaterialLines(getSessionMaterials(sessionId));
  if (remaining.length) {
    saveSessionMaterials(sessionId, remaining);
    throw (
      result.error ||
      new Error("Materials persistence not confirmed; retaining local drafts.")
    );
  }

  if (!result.confirmed) {
    throw (
      result.error ||
      new Error("Materials persistence not confirmed; retaining local drafts.")
    );
  }

  clearSessionMaterials(sessionId);
  return result;
}

export function getSessionMaterialSyncSummary(lines = []) {
  const active = (lines || []).filter((line) => !line.voided_at);
  if (!active.length) {
    return { kind: "empty", label: "" };
  }

  const hasError = active.some(
    (line) => line.sync_status === SESSION_MATERIAL_SYNC_STATUS.ERROR,
  );
  if (hasError) {
    return {
      kind: "error",
      label: "Materials could not be saved — Retry",
    };
  }

  const syncing = active.some(
    (line) =>
      line.sync_status === SESSION_MATERIAL_SYNC_STATUS.SYNCING ||
      line.sync_status === SESSION_MATERIAL_SYNC_STATUS.PENDING,
  );
  if (syncing) {
    return {
      kind: "syncing",
      label: "Saved locally — syncing",
    };
  }

  if (active.every(isSessionMaterialLineSaved)) {
    return { kind: "saved", label: "Materials saved" };
  }

  return { kind: "syncing", label: "Saved locally — syncing" };
}
